/**
 * AWS WAF Integration utilities.
 *
 * - getWafHeaders()             – silent challenge token (challenge.js / AwsWafIntegration)
 * - renderCaptcha(el)           – interactive CAPTCHA widget (jsapi.js / AwsWafCaptcha)
 * - handleWafCaptchaChallenge() – detects a 405 CAPTCHA challenge and auto-renders the widget
 * - wafInertiaPost()            – Inertia POST with silent challenge + CAPTCHA fallback
 */

import { Inertia } from '@inertiajs/inertia';
import { store } from '../store';

const WAF_API_KEY = process.env.MIX_AWF_API_KEY || '';

/**
 * Obtain a silent-challenge WAF token and return it as a header object.
 * Falls back to an empty object when the SDK is not loaded.
 */
export async function getWafHeaders() {
  const headers = {};
  if (typeof AwsWafIntegration !== 'undefined') {
    try {
      const token = await AwsWafIntegration.getToken();
      headers['x-aws-waf-token'] = token;
    } catch (e) {
      // Continue without token if WAF integration is unavailable
    }
  }
  return headers;
}

/**
 * Render an interactive AWS WAF CAPTCHA inside `container`.
 * Returns a Promise that resolves with the CAPTCHA token.
 *
 * @param {HTMLElement} container – DOM element where the CAPTCHA will be rendered
 * @param {Object}      options  – extra options forwarded to AwsWafCaptcha.renderCaptcha
 */
export function renderCaptcha(container, options = {}) {
  return new Promise((resolve, reject) => {
    if (typeof AwsWafCaptcha === 'undefined') {
      return reject(new Error('AWS WAF CAPTCHA SDK (jsapi.js) not loaded'));
    }

    AwsWafCaptcha.renderCaptcha(container, {
      apiKey: WAF_API_KEY,
      skipTitle: true,
      onSuccess: (wafToken) => resolve(wafToken),
      onError: (err) => reject(err),
      ...options,
    });
  });
}

/**
 * Inspect an Inertia/Axios error response.
 * If WAF responded with a CAPTCHA challenge (status 405) render the CAPTCHA
 * inside `container` and return the new token; otherwise re-throw.
 *
 * @param {Error|Object} error     – the error from the failed request
 * @param {HTMLElement}  container – DOM element for the CAPTCHA widget
 * @returns {Promise<string>}      – WAF token obtained via CAPTCHA
 */
export async function handleWafCaptchaChallenge(error, container) {
  const status = error?.response?.status ?? error?.status;
  if (status === 405) {
    return renderCaptcha(container);
  }
  throw error;
}

/**
 * Perform an Inertia POST with WAF silent challenge token and automatic
 * CAPTCHA fallback.  If WAF responds with a 405 (CAPTCHA challenge), the
 * interactive CAPTCHA widget is rendered inside `captchaContainer` and the
 * request is retried once with the solved token.
 *
 * @param {Object}       inertia            – Vue Inertia instance (this.$inertia)
 * @param {string}       url                – POST endpoint
 * @param {Object}       data               – form data
 * @param {Object}       options            – Inertia visit options (onSuccess, onError, …)
 * @param {HTMLElement}  captchaContainer   – DOM element for the CAPTCHA widget (Vue $refs)
 * @param {Function}     onCaptchaRequired  – optional callback invoked before rendering
 *                                            (use to open a modal so the container is visible)
 * @param {Function}     onCaptchaDone      – optional callback invoked after CAPTCHA resolves
 *                                            or is dismissed/fails (use to close the modal)
 */
export async function wafInertiaPost(inertia, url, data, options = {}, captchaContainer = null, onCaptchaRequired = null, onCaptchaDone = null) {
  const wafHeaders = await getWafHeaders();
  const mergedHeaders = { ...options.headers, ...wafHeaders };

  let removeInvalidListener = null;

  if (captchaContainer) {
    removeInvalidListener = Inertia.on('invalid', (event) => {
      const response = event.detail.response;

      if (response.status === 429) {
        event.preventDefault();

        if (removeInvalidListener) {
          removeInvalidListener();
          removeInvalidListener = null;
        }

        if (options.onError) options.onError();

        store.commit('showFlashMessage', {
          message: 'Too Many Attempts. Please try again later.',
          type: 'danger',
        });

        return;
      }

      if (response.status === 405) {
        event.preventDefault();

        // Clean up the listener immediately so it doesn't fire twice
        if (removeInvalidListener) {
          removeInvalidListener();
          removeInvalidListener = null;
        }

        if (onCaptchaRequired) onCaptchaRequired();

        // Allow one Vue tick so v-show/v-if has time to make the container visible
        Promise.resolve().then(() =>
          renderCaptcha(captchaContainer)
            .then((captchaToken) => {
              // CAPTCHA solved — close the modal, then retry with the new token
              if (onCaptchaDone) onCaptchaDone();
              inertia.post(url, data, {
                ...options,
                headers: { ...mergedHeaders, 'x-aws-waf-token': captchaToken },
              });
            })
            .catch(() => {
              // CAPTCHA dismissed or failed — close the modal and clear the container
              if (onCaptchaDone) onCaptchaDone();
              if (captchaContainer) captchaContainer.innerHTML = '';
            })
        );
      }
    });
  }

  const originalOnFinish = options.onFinish;

  inertia.post(url, data, {
    ...options,
    headers: mergedHeaders,
    onFinish(...args) {
      // Remove the invalid listener when the visit completes normally
      if (removeInvalidListener) {
        removeInvalidListener();
        removeInvalidListener = null;
      }
      if (originalOnFinish) originalOnFinish(...args);
    },
  });
}
